// Import components
import CreateFarmCalcAccordion from './CreateFarmCalcAccordion';
import CreateBottomStepNavigation from './Navigation/CreateBottomStepNavigation';
import CreateTopStepNavigation from './Navigation/CreateTopStepNavigation';
import CreateErrorMessage from './CreateErrorMessage';
import CreateSummary from './Calculations/Summary/Summary';
import CreateFinish from './Calculations/Finish/Finish';

// Import field values
import {
    PropertyDetailsFieldValues,
    LivestockFieldValues,
    GardenWaterUseFieldValues,
    DairyShedWaterUseFieldValues,
    GroundwaterCreeksRiversValues,
    PotentialCatchmentSupplyGroundCoverFieldValues,
    PotentialCatchmentSupplySoilProfileFieldValues,
    DamVolumeFieldValues
} from './Fields/FieldValues';

// Import utils
import { getDocumentElement, getChildren, setCorrespondingFieldValues } from './Utils/Utils';

// Import nav functionality
import OpenCloseNavigation from './Navigation/OpenCloseNavigation';

// When window is loaded, identify the individual sections.
window.addEventListener('load', () => {
    const SectionSelectors = [
        '.farm-calc-section--start',
        '.farm-calc-section--use',
        '.farm-calc-section--supply',
        '.farm-calc-section--storage',
        '.farm-calc-section--summary',
        '.farm-calc-section--finish'
    ];
    let Sections = [];

    // Loop through the given section classes and save the valid elements.
    SectionSelectors.forEach(Selector => {
        const El = getDocumentElement(Selector);

        if (El instanceof HTMLElement) {
            Sections.push(El);
        }
    });

    // If we have sections present, loop through them.
    // We also want to init all related components
    if (Sections.length > 0) {
        Sections.forEach(Section => {
            const FarmCalcAccordions = getChildren(Section, '.farm-calc-accordion');
            let skipCounter = 0;

            if (FarmCalcAccordions.length > 0) {
                FarmCalcAccordions.forEach((Accordion, index) => {
                    if (Accordion instanceof HTMLElement) {
                        const Nested = Accordion.classList.contains('farm-calc-accordion--nested');

                        if (Nested) {
                            skipCounter += 1;
                        }

                        const FarmCalcAccordion = CreateFarmCalcAccordion({
                            farmCalcAccordion: Accordion,
                            animationTime: 200,
                            index: (index + 1) - skipCounter,
                            isClone: false,
                            nested: Nested,
                            attributes: {
                                mount: 'data-mount',
                                id: 'data-id'
                            },
                            classes: {
                                active: 'active',
                                updated: 'updated'
                            },
                            selectors: {
                                button: '.farm-calc-accordion__button',
                                container: '.farm-calc-accordion__container',
                                dataset: '.farm-calc-accordion-dataset',
                                dropdown: '.farm-calc-accordion__dropdown',
                                number: '.farm-calc-accordion__button-number',
                                title: '.farm-calc-accordion__button-title'
                            },
                        });

                        FarmCalcAccordion.init();
                    }
                });
            }
        });

        // # # # # # # # # # # #
        // Setup step navigation
        // # # # # # # # # # # #
        const FarmCalcStepsBottomNavigation = new CreateBottomStepNavigation({
            steps: Sections,
            classes: {
                active: 'active',
            },
            selectors: {
                back: '.farm-calc-bottom-navigation__button--back',
                next: '.farm-calc-bottom-navigation__button--next',
                step: '.farm-calc-section',
                stepText: '.farm-calc-bottom-navigation__indicator',
            }
        });

        const FarmCalcStepsTopNavigation = new CreateTopStepNavigation({
            steps: Sections,
            attributes: {
                step: 'data-step',
            },
            classes: {
                active: 'active',
            },
            selectors: {
                link: '.farm-calc-top-navigation__list-link',
                step: '.farm-calc-section',
            }
        });

        // # # # # # # # # # # # # # # # # # # # # # # # # #
        // Setup a basic error message handler
        // # # # # # # # # # # # # # # # # # # # # # # # # #
        const ErrorMessageMM = new CreateErrorMessage({
            classes: {
                active: 'active'
            },
            selectors: {
                error: '.farm-calc-accordion__error--mm',
                field: '#rainfall'
            }
        });

        const ErrorMessageDivide = new CreateErrorMessage({
            classes: {
                active: 'active'
            },
            selectors: {
                error: '.farm-calc-accordion__error--divide',
                field: '#locationNS'
            }
        });

        // # # # # # # # # # # # # # # # # # # # # # # # # #
        // Setup summary fields
        // # # # # # # # # # # # # # # # # # # # # # # # # #
        const Summary = CreateSummary({
            selectors: {
                section: '.farm-calc-section--summary',
                accordion: '.farm-calc-accordion',
                topNavButton: '.farm-calc-top-navigation__list-link',
                bottomNavButton: '.farm-calc-bottom-navigation__button',
            }
        });

        // # # # # # # # # # # # # # # # # # # # # # # # # #
        // Setup finish fields
        // # # # # # # # # # # # # # # # # # # # # # # # # #
        const Finish = CreateFinish({
            selectors: {
                section: '.farm-calc-section--finish',
                topNavButton: '.farm-calc-top-navigation__list-link',
                bottomNavButton: '.farm-calc-bottom-navigation__button',
            }
        });

        // # # # # # # # # # # # # # # # # # # # # # # # # #
        // Setup the fields that need to be initialised once
        // # # # # # # # # # # # # # # # # # # # # # # # # #
        // Property details fields (non clonable)
        setCorrespondingFieldValues(
            PropertyDetailsFieldValues,
            {
                isClone: false,
                primaryField: '#propDetails',
                secondaryField: '#rainfall',
                tertiaryField: '#evaporation',
                updateSecondaryOptions: false
            },
        );

        // Livestock fields (clonable)
        setCorrespondingFieldValues(
            LivestockFieldValues,
            {
                isClone: false,
                primaryField: '#livestockType-1',
                secondaryField: '#livestockSub-1',
                tertiaryField: '#livestockMegal-1',
                updateSecondaryOptions: true,
                updateTertiaryOptions: false
            },
        );

        // Garden fields (clonable)
        setCorrespondingFieldValues(
            GardenWaterUseFieldValues,
            {
                isClone: false,
                primaryField: '#gardenType-1',
                secondaryField: '',
                tertiaryField: '',
                updateSecondaryOptions: false,
                updateTertiaryOptions: false
            },
        );

        // Dairy shed fields (clonable)
        setCorrespondingFieldValues(
            DairyShedWaterUseFieldValues,
            {
                isClone: false,
                primaryField: '#dairyType-1',
                secondaryField: '#herdSize-1',
                tertiaryField: '#dairyTotal-1',
                updateSecondaryOptions: true,
                updateTertiaryOptions: true
            },
        );

        // Groundwater creeks and rivers (clonable)
        setCorrespondingFieldValues(
            GroundwaterCreeksRiversValues,
            {
                isClone: false,
                primaryField: '#supplyType-1',
                secondaryField: '',
                tertiaryField: '',
                updateSecondaryOptions: false,
                updateTertiaryOptions: false
            },
        );

        // Potential catchment supply soil profile (clonable)
        setCorrespondingFieldValues(
            PotentialCatchmentSupplySoilProfileFieldValues,
            {
                isClone: false,
                primaryField: '#soilProfile-1',
                secondaryField: '',
                tertiaryField: '',
                updateSecondaryOptions: false,
                updateTertiaryOptions: false
            },
        );

        // Potential catchment supply ground cover (clonable)
        setCorrespondingFieldValues(
            PotentialCatchmentSupplyGroundCoverFieldValues,
            {
                isClone: false,
                primaryField: '#groundCover-1',
                secondaryField: '',
                tertiaryField: '',
                updateSecondaryOptions: false,
                updateTertiaryOptions: false
            },
        );

        // Dam volume (clonable)
        setCorrespondingFieldValues(
            DamVolumeFieldValues,
            {
                isClone: false,
                primaryField: '#dam_type-1',
                secondaryField: '',
                tertiaryField: '',
                updateSecondaryOptions: false,
                updateTertiaryOptions: false
            },
        );

        // # # # # # # # # # # #
        // Init imported scripts
        // # # # # # # # # # # #
        FarmCalcStepsBottomNavigation.init();
        FarmCalcStepsTopNavigation.init();
        ErrorMessageMM.init();
        ErrorMessageDivide.init();
        Summary.init();
        Finish.init();
        OpenCloseNavigation();
    }
    
});
